import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { DailyStatusBody } from '../daily/DailyStatusView'
import { PairCard } from '../daily/PairCard'
import { SelfReportForm } from '../journal/SelfReportForm'
import { Button, Card, EmptyState, Notice, ProgressBar, RecordingStatusPill } from '../ui'
import { getDailyStatus, getRecording, listDayRecordings, type Job, type RecordingRecord } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import { readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { deviceLabel } from '../../lib/devices'
import { EXPERIMENTS_ENABLED, PAIRED_SESSION_ENABLED } from '../../lib/features'
import { formatLocalClock } from '../../lib/format'
import { pairLinkOf } from '../../lib/pair'
import { isProblem } from '../../lib/problem'
import { RECORDING_OUTCOME } from '../../lib/recordings'
import { isTerminalJob, type useJobPolling } from '../../lib/useJobPolling'
import { PHASE_WORDS, phaseLabel } from './phaseWords'

/**
 * Watching a recording job, and the four ways it ends.
 *
 * Lifted out of `pages/product/Record.tsx` in Sprint 1.9.7 so the capture flow
 * (`/record/connect`) and the testing upload share ONE reading of a job: the
 * code before the status, no guessed recording for an interrupted job, the
 * Daily Status shown inline, the journal offered only where it has not already
 * been asked. Every comment below is the original's; only the module moved.
 *
 * `words` lets the two hosts name their own next action ("Upload another" on
 * the testing page, "Record another" after a capture) without either learning
 * the other's vocabulary.
 */
export interface JobFlowWords {
  again: string
}

const UPLOAD_WORDS: JobFlowWords = { again: 'Upload another' }

/* ── watching, and the four ways it ends ────────────────────────────────── */

export function JobWatch({
  poll,
  onStartOver,
  words = UPLOAD_WORDS,
  journal = true,
  revealResult = true,
}: {
  poll: ReturnType<typeof useJobPolling>
  onStartOver: () => void
  words?: JobFlowWords
  /** Whether to offer the journal form after a result. False where it was asked before processing. */
  journal?: boolean
  /** EXP-007 waits for the run/report gate; job completion must not fetch a result. */
  revealResult?: boolean
}) {
  const { job, isPaused, retry } = poll

  if (isPaused) {
    return (
      <Card eyebrow="Processing">
        <Notice tone="warn">
          <p>
            We lost contact while your recording was being processed. It is still being
            worked on — this page just could not check.
          </p>
          <Button variant="secondary" size="sm" onClick={retry}>
            Check again
          </Button>
        </Notice>
      </Card>
    )
  }

  if (job === null) {
    return (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Looking for your recording…
        </p>
      </Card>
    )
  }

  if (!isTerminalJob(job.status)) {
    return (
      <Card eyebrow="Processing" active>
        {/* `pending` is genuinely not started, and a job returned to `pending`
            by a retry can still be carrying its previous phase -- so nothing
            lights for one. */}
        <PhaseRail
          phase={job.status === 'pending' ? null : (job.phase ?? null)}
          done={job.status === 'pending' ? new Set() : donePhases(job)}
        />
        <ProgressBar
          label={phaseLabel(job)}
          // Exactly as received. `progress_pct` is NOT monotonic across a retried
          // attempt -- a worker failure returns the job to `pending` and the next
          // attempt re-emits from the start -- so clamping it to a previous
          // maximum would freeze the bar and make a working retry look stuck.
          value={job.status === 'pending' ? null : job.progress_pct}
          detail="You can leave this page. It will pick up where it left off."
        />
      </Card>
    )
  }

  return <JobOutcome job={job} onStartOver={onStartOver} words={words} journal={journal} revealResult={revealResult} />
}

/**
 * The phases this job has finished — the BACKEND's ordering, never a guess.
 *
 * R22 allowed only the current phase, and its reason was exact: `Job.phase` had
 * no declared order and `progress_pct` is not monotonic across a retried
 * attempt, so "the ones before this one" was not a fact this client had. It is
 * one now. `phase_sequence` and `phase_index` (ask B5) are derived backend-side
 * from the job's KIND, because RECORD emits six phases and CALIBRATE nine.
 *
 * Three ways this returns nothing, all of them deliberate:
 *   * `phase_index` null — the job has not started, or reports a phase this
 *     backend does not know. Marking nothing is the honest answer; marking
 *     everything up to a guess is not.
 *   * a retried attempt resets `phase` to null, so the rail empties with it
 *     rather than keeping stale ticks from the previous run.
 *   * a payload from before the field shipped carries neither key.
 */
function donePhases(job: Job): ReadonlySet<string> {
  if (job.phase_index == null) return new Set()
  return new Set((job.phase_sequence ?? []).slice(0, job.phase_index))
}

/**
 * Where the job is, as bars — and ONLY where it is.
 *
 * ── NO "DONE" STATE, AND NO WORDS ───────────────────────────────────────────
 *
 * The canvas paints every phase before the current one green. `Job.phase` has
 * no declared order (`api.types.ts`), `progress_pct` is not monotonic across a
 * retried attempt, and an unrecognised phase renders as itself — so "the ones
 * before this one" is not a fact this client has. Lighting them would be the
 * client narrating work it cannot see. Backend ask B5.
 *
 * The cells carry NO TEXT. R22 allows either decorative cells or a job row
 * without the words; cells that carry words put EVERY phase's words in the DOM
 * for the whole job, and `getByText('Cleaning the signal')` would then pass
 * whatever the job's phase is — `aria-hidden` does not hide text from
 * `getByText`. The words are rendered once, by `ProgressBar`'s label.
 *
 * The column count IS the number of children (`grid-auto-flow: column`), so
 * adding a phase to `PHASE_WORDS` needs no second edit and no integer anywhere.
 */
export function PhaseRail({ phase, done }: { phase: string | null; done: ReadonlySet<string> }) {
  return (
    <div className="phases" aria-hidden="true">
      {Object.keys(PHASE_WORDS).map((key) => (
        <div
          key={key}
          className={
            key === phase
              ? 'phase phase--current'
              : done.has(key)
                ? 'phase phase--done'
                : 'phase'
          }
          data-phase={key}
        >
          <div className="phase__bar" />
        </div>
      ))}
    </div>
  )
}

export function JobOutcome({
  job,
  onStartOver,
  words = UPLOAD_WORDS,
  journal = true,
  revealResult = true,
}: {
  job: Job
  onStartOver: () => void
  words?: JobFlowWords
  journal?: boolean
  revealResult?: boolean
}) {
  const problem = isProblem(job.error_problem) ? job.error_problem : null

  /**
   * ── THE CODE BEFORE THE STATUS ─────────────────────────────────────────────
   *
   * A recording with no scoreable signal produces a job whose status is
   * `failed`, whose code is `recording.empty`, and which has no
   * `result_recording_id`. Reading the status first labels it "Failed", which is
   * the one thing acceptance criterion 8 forbids: the recording did not fail, it
   * held nothing.
   */
  if (problem?.code === 'recording.empty') {
    const outcome = RECORDING_OUTCOME.empty!
    return (
      <Card eyebrow="Finished">
        <EmptyState
          title={outcome.label}
          align="start"
          action={
            <div className="record-outcome__actions">
              {/* History, not Today, and for the same reason as the branch below.
                  An `empty` recording IS a row in History -- Sprint 1.2 §9 split
                  it out of `rejected` precisely so it could be terminal history
                  with no result -- so this link takes the person to the session
                  they just uploaded. Today would show them a different day's
                  result. The framing stays §2.6's: nothing here failed. */}
              <Link className="btn btn--primary" to="/history">
                See it in your history
              </Link>
              <Button variant="ghost" onClick={onStartOver}>
                {words.again}
              </Button>
            </div>
          }
        >
          <p className="product-empty__body">{outcome.detail}</p>
        </EmptyState>
      </Card>
    )
  }

  if (job.status === 'succeeded' && !revealResult) {
    return (
      <Card eyebrow="Reading">
        <h2 className="guide__title">Processing finished.</h2>
        <Button variant="secondary" onClick={onStartOver}>{words.again}</Button>
      </Card>
    )
  }

  if (job.status === 'succeeded' && job.result_recording_id != null) {
    return <SucceededOutcome recordingId={job.result_recording_id} onStartOver={onStartOver} words={words} journal={journal} />
  }

  /**
   * `failed` and `interrupted`, and the vanishingly rare `succeeded` with no
   * result id.
   *
   * §2.3: *"When a failed/interrupted Job has no `result_recording_id`, show its
   * Problem and refresh History; do not guess that the newest recording belongs
   * to that Job."*
   *
   * The Problem half and the refusal to guess were right from the start. The
   * link went to `/today`, which is the half that was not -- and it was not a
   * one-word swap, because "Back to Today" is copy about Today rather than
   * navigation.
   *
   * The reasoning that made `/today` look harmless does not hold: `Today.tsx`
   * filters RECORDINGS later than the result it is showing, and this branch is
   * reached precisely when the job named no recording. Whether a row exists for
   * it at all is a backend matter this client cannot determine -- which is the
   * same thing §2.3 forbids guessing about. History is where the durable list
   * lives, which is why the clause names it, and it re-reads on arrival
   * (`History.tsx` keys its list on the account and `usePagedList` fetches on
   * mount), so the link satisfies "refresh".
   */
  return (
    <Card eyebrow="Finished">
      <EmptyState
        title="This recording could not be processed"
        align="start"
        action={
          <div className="record-outcome__actions">
            <Link className="btn btn--secondary" to="/history">
              Go to your history
            </Link>
            <Button variant="ghost" onClick={onStartOver}>
              {words === UPLOAD_WORDS ? 'Try another upload' : words.again}
            </Button>
          </div>
        }
      >
        <p className="product-empty__body">
          {problem?.detail ?? 'The processor did not finish, and did not say why.'}
        </p>
        {problem?.code != null && (
          <p className="product-empty__body record-outcome__reference">
            Reference: <code className="mono-cell product-id">{problem.code}</code>
          </p>
        )}
      </EmptyState>
    </Card>
  )
}

/**
 * A job that succeeded still has four possible recordings behind it.
 *
 * `scored` is the one everybody pictures. `rejected` is a quality decision with a
 * reason attached, and it is shown. The status on the record is what says whether
 * to ask for a Daily Status at all — asking for one that does not exist
 * manufactures a 404 that looks like an error.
 */
export function SucceededOutcome({
  recordingId,
  onStartOver,
  words = UPLOAD_WORDS,
  journal = true,
}: {
  recordingId: string
  onStartOver: () => void
  words?: JobFlowWords
  journal?: boolean
}) {
  const [recording, setRecording] = useState<RecordingRecord | null>(null)
  const [status, setStatus] = useState<DailyStatusResult | null>(null)
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const record = await getRecording(recordingId)
        if (cancelled) return
        setRecording(record)
        if (RECORDING_OUTCOME[record.status]?.hasResult === true) {
          const payload = await getDailyStatus(recordingId)
          if (cancelled) return
          setStatus(readDailyStatus(payload))
        }
      } catch {
        // The recording exists -- the job said so. A read that failed is worth
        // no more than a quieter screen; History is the durable home.
        if (!cancelled) setRecording(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordingId])

  if (isLoading) {
    return (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Fetching your result…
        </p>
      </Card>
    )
  }

  const pair = PAIRED_SESSION_ENABLED && recording !== null ? pairLinkOf(recording) : null

  return (
    <>
      <Card eyebrow="Finished">
        <EmptyState
          title={
            recording === null ? 'Processed' : (RECORDING_OUTCOME[recording.status]?.label ?? 'Processed')
          }
          align="start"
          action={
            /*
              ── "SEE IT ON TODAY" WAS A PROMISE THIS APP CANNOT KEEP ──────────
              Today shows the newest SCORED recording by `session_date`
              (`Today.tsx:73`), so a `rejected` upload appears there only as a
              side notice, and a backfilled one whose recording date is older
              than an existing result is not shown at all. Two of the four
              outcomes this screen can reach therefore sent a person to a page
              that does not show what they just uploaded. Its own detail page
              always does, and it exists as of Stage 6.
            */
            <div className="record-outcome__actions">
              <Link
                className="btn btn--primary"
                to={`/history/${encodeURIComponent(recordingId)}`}
              >
                See this recording
              </Link>
              {EXPERIMENTS_ENABLED && recording?.purpose === 'extra' && recording.status === 'scored' && (
                <ExtraCompareLink recording={recording} />
              )}
              <Button variant="ghost" onClick={onStartOver}>
                {words.again}
              </Button>
            </div>
          }
        >
          {recording !== null && RECORDING_OUTCOME[recording.status]?.detail != null && (
            <p className="product-empty__body">{RECORDING_OUTCOME[recording.status]?.detail}</p>
          )}
          {recording !== null && (
            <p>
              <RecordingStatusPill status={recording.status} />
              {/* The headband the backend RESOLVED from the archive, not the one
                  the page believed it was talking to: `device_id` on the stored
                  row is the profile it was analysed as (Sprint 1.9.2 S4). */}
              {deviceLabel(recording.device_id) !== null && (
                <span className="record-outcome__device"> · {deviceLabel(recording.device_id)}</span>
              )}
            </p>
          )}
        </EmptyState>
      </Card>
      {/*
        ── THE JOURNAL SITS ABOVE THE RESULT, AND DOES NOT GATE IT ──────────────
        §3.1 puts a short, optional form after the upload. It is rendered BEFORE
        the Daily Status because `SelfReport`'s own contract says the journal is
        "captured after the recording, before the reveal, so it stays a clean
        validation label" -- an account of the day written after reading a score is
        no longer independent of it. It does not WAIT for the journal, though:
        §3.4 is explicit that a self-report never blocks the Daily Status.

        `recording === null` means the read FAILED (the catch above is deliberate),
        not that there is no journal. Without the stored report there is nothing to
        diff against and a pre-filled form would be a guess, so the form is not
        offered at all -- History is the durable home for it.

        An `empty` recording never reaches here: its job FAILED with
        `recording.empty` and carries no `result_recording_id`, so there is no id
        to patch. A `rejected` one does, and gets the form: the day happened even
        if the signal did not pass quality, and the backend applies no status gate.
      */}
      {journal && recording !== null && (
        <SelfReportForm
          recordingId={recordingId}
          original={{
            loaded: true,
            report: recording.self_report ?? null,
            provenance: recording.self_report_provenance ?? null,
          }}
          onSaved={setRecording}
        />
      )}
      {status !== null && status.readable && (
        <DailyStatusBody
          status={status}
          // The same two ROW facts Today and the recording page hand the body,
          // so the recording card names the condition and the headband here too.
          recording={
            recording === null
              ? undefined
              : { condition: recording.condition, deviceId: recording.device_id }
          }
        />
      )}
      {status !== null && !status.readable && <Notice tone="warn">{status.detail}</Notice>}
      {/* The paired session (Sprint 1.9.13 W4): the eyes-closed block's result
          is followed by the pair's card, exactly as on Today and on its own
          page. The capture flow lands here, so this is where the person first
          sees it. */}
      {pair !== null && <PairCard recordingId={recordingId} pairRole={pair.pairRole} />}
    </>
  )
}

/** The server's extra result points back to its own recorded day, including a backfill. */
function ExtraCompareLink({ recording }: { recording: RecordingRecord }) {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  const experimentsForMe = account?.experiments_enabled_for_me === true
  const [found, setFound] = useState<{ recordingId: string; userId: string; daily: RecordingRecord } | null>(null)
  useEffect(() => {
    if (!experimentsForMe || userId === null || userId !== recording.user_id || recording.local_date == null) return
    const abort = new AbortController()
    void listDayRecordings(userId, recording.local_date, { signal: abort.signal }).then((rows) => {
      const daily = rows.find((row) => row.is_daily_reading && row.status === 'scored' && row.recording_id !== recording.recording_id)
      if (daily !== undefined && !abort.signal.aborted) setFound({ recordingId: recording.recording_id, userId, daily })
    }).catch(() => {
      // The recording result remains readable if its optional comparison link cannot be resolved.
    })
    return () => abort.abort()
  }, [experimentsForMe, userId, recording.user_id, recording.local_date, recording.recording_id])

  if (!experimentsForMe || found === null || found.userId !== userId || found.recordingId !== recording.recording_id) return null
  return (
    <Link className="btn btn--secondary" to={`/history/compare/${encodeURIComponent(found.daily.recording_id)}/${encodeURIComponent(recording.recording_id)}`}>
      Compare with {formatLocalClock(found.daily.start_local_time)}
    </Link>
  )
}

